import React from "react";
import {
  FiCreditCard,
  FiMonitor,
  FiShoppingCart,
  FiExternalLink,
  FiMail,
  FiBook,
} from "react-icons/fi";

const demoOptions = [
  {
    id: 1,
    title: "Merchant Portal",
    description:
      "See how merchants manage transactions, smart route transactions, view reports, and handle refunds — all in one place.",
    icon: FiMonitor,
    href: "https://tnsportalui.uat.paymentgateway.tnsi.com/?tenantid=1ed7b1ec-060e-447b-a209-20cdff3580a6&portalType=merchant",
    external: true,
  },
  {
    id: 2,
    title: "Hosted Checkout",
    description:
      "Show customers a TNS' Hosted Checkout — accept card and alternative payments e.",
    icon: FiShoppingCart,
    href: "/shoppingcart",
    external: false,
  },
  {
    id: 3,
    title: "Attended Terminal — PAX A920 Pro",
    description:
      "TNS' in-person, attended payment terminal. Perfect for attended retail and valet payments.",
    icon: FiCreditCard,
    href: "/terminal",
    external: false,
  },
  {
    id: 4,
    title: "Unattended — Self 5000",
    description:
      "TNS' robust Ingenico Self Unattended terminal — suitable for parking, vending, laundromats.",
    icon: FiCreditCard,
    href: "/unattendedself5000",
    external: false,
  },
  {
    id: 5,
    title: "Unattended — PAX IM30",
    description:
      "TNS' next generation, Android based Unattended terminal — suitable for parking, vending, laundromats and unattended fuel",
    icon: FiCreditCard,
    href: "/unattendedpaxim30",
    external: false,
  },
  {
    id: 6,
    title: "Outdoor Payment Terminal (OPT)",
    description:
      "TNS Outdoor Payment Terminal — built for fuel pumps, EV chargers, and outdoor environments.",
    icon: FiCreditCard,
    href: "/opt",
    external: false,
  },
];

export default function PaymentDemo() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - matches /ask page style */}
      <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
              <FiCreditCard className="w-4 h-4 mr-2" />
              Live Payment Demos
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
              Payment
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {" "}Demo
              </span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Pick a demo below and show your customer exactly how TNSPay works.
              No setup required — just click and go.
            </p>
          </div>
        </div>
      </section>

      {/* Demo Cards Section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {demoOptions.map((demo) => {
              const Icon = demo.icon;
              return (
                <a
                  key={demo.id}
                  href={demo.href}
                  target={demo.external ? "_blank" : undefined}
                  rel={demo.external ? "noopener noreferrer" : undefined}
                  className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:border-blue-300 hover:shadow-lg transition-all duration-300 group text-left"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {demo.title}
                        </h3>
                        {demo.external && (
                          <FiExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {demo.description}
                      </p>
                      <span className="inline-block text-sm font-medium text-blue-600 pt-1 group-hover:text-blue-700">
                        Launch Demo →
                      </span>
                    </div>
                  </div>
                </a>
              );
            })}

            {/* Developer Portal - same card style */}
            <a
              href="https://developer.pay.tnsi.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm hover:border-blue-300 hover:shadow-lg transition-all duration-300 group text-left"
            >
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                  <FiBook className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                      Developer Portal
                    </h3>
                    <FiExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    API docs, integration guides, and sandbox access — for the technical conversations.
                  </p>
                  <span className="inline-block text-sm font-medium text-blue-600 pt-1 group-hover:text-blue-700">
                    Open Portal →
                  </span>
                </div>
              </div>
            </a>

            {/* Contact - same card style */}
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FiMail className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 space-y-1">
                  <h3 className="text-base font-semibold text-gray-900">
                    Need Merchant Portal Access?
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Contact{" "}
                    <a
                      href="mailto:dhore@tnsi.com"
                      className="text-blue-600 font-medium hover:underline"
                    >
                      David Hore (dhore@tnsi.com)
                    </a>{" "}
                    to get set up with credentials.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
